import { CboRole, FeatureFlag } from '@cbo/shared-library';
import DomainIcon from '@mui/icons-material/Domain';
import Help from '@mui/icons-material/Help';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Settings from '@mui/icons-material/Settings';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  ApplicationSwitcher,
  RccEventKeys,
  UserProfileOption,
  useRccContext,
  useSiteContext,
} from '@ncr-voyix-commerce/react-common-components';
import { useOktaAuth } from '@okta/okta-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useShell } from '../../../pages/Shell';
import InternalSiteSelector, { organizationViewSite } from '../../admin/SiteSelector/SiteSelector';
import routes from '../../constants/routes';
import { useSites } from '../../contexts/siteContext';
import { useUsers } from '../../contexts/userContext';
import isPermitted from '../../lib/permissions';
import { useOrgContext } from '../../org/CommonComponentWrapper';
import OrganizationSwitcher from '../../org/OrganizationSwitcher';
import BspSiteSwitcherWrapper, { SiteSwitcherOptions } from '../../org/SiteSwitcher/SiteSwitcher';
import UserProfileSettings from '../../org/UserProfile';
import recordLogoutEvent from '../../services/AuditService';
import { useFeatureFlag } from '../../utils/hooks/useFeatureFlag';
import { useSidebar } from '../SidebarNavigation/ncr-design-system-components/CustomSidebar/CustomSidebarContext';
import { authenticatedActions, loginButton, menuIcon, notificationsIcon, outerBox } from './styles';
import { isOrgViewRoute } from '../../admin/utils/siteUtils';

interface AppBarProps {
  hasUnreadNotifications: boolean;
}

export const APPBAR_HEIGHT = '72px';

/** The content that will go in the app bar */
function AppBar({ hasUnreadNotifications }: AppBarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setCollapsed, isCollapsed } = useSidebar();
  const { t } = useTranslation();
  const { setRightPanel } = useShell();
  const { pathname: currentUrl, search: urlParams } = useLocation();
  const bslSiteSwitcher = useFeatureFlag(FeatureFlag.siteSwitcher);
  const user = useUsers();
  const history = useHistory();
  const [isOrgView, setIsOrgView] = useState(false);
  const [isDrillDownView, setIsDrillDownView] = useState(false);
  const { organization, userOrganizations } = useOrgContext();
  const { isAuthenticated, events } = useRccContext();
  const { selectedSite } = useSites();
  const { selectedSite: bslSelectedSite } = useSiteContext();
  const isPreviewUrl = process.env.REACT_APP_USE_PREVIEW_ORG_SWITCHER === 'true';
  const showSitePicker = !user.isOrgSwitching && organization;
  const useInternalSiteSelector = !bslSiteSwitcher || isPreviewUrl;

  useEffect(() => {
    if (isOrgViewRoute(currentUrl)) {
      setIsOrgView(true);
    } else {
      setIsOrgView(false);
    }

    if (
      [
        routes.EMPLOYEE_OVERVIEW_BASE,
        routes.HOUSE_ACCOUNT_FOCUSMODE_BASE,
        routes.JOBCODE_CONFIGURATION_FOCUSMODE_BASE,
        routes.JOBCODE_EMPLOYEES_FOCUSMODE_BASE,
      ].some((focusModePath) => currentUrl.includes(focusModePath) && currentUrl.split(focusModePath)[1].length <= 1)
    ) {
      setIsDrillDownView(false);
    } else if (
      [
        routes.EMPLOYEE_PAGE,
        routes.JOBCODE_CONFIGURATION_PAGE,
        routes.INVOICES,
        routes.RAW_ITEMS,
        routes.HOUSE_ACCOUNTS,
        routes.NOTIFICATION_SETTINGS,
        routes.LABOR_RULES,
        routes.PUNCH_SUMMARY,
      ].some((baseRoutes) => currentUrl.includes(baseRoutes) && currentUrl.split(baseRoutes)[1].length > 1)
    ) {
      setIsDrillDownView(true);
    } else {
      setIsDrillDownView(false);
    }
  }, [currentUrl]);

  useEffect(() => {
    const isOrg =
      isOrgView ||
      bslSelectedSite?.id === SiteSwitcherOptions.ORGANIZATION_VIEW ||
      selectedSite.name === organizationViewSite.name;

    if (user?.org?.bslLongName && bslSelectedSite?.id) {
      const existingSearchParams = new URLSearchParams(urlParams);
      existingSearchParams.set('organizationId', user.org.bslLongName);
      existingSearchParams.set('siteId', isOrg ? SiteSwitcherOptions.ORGANIZATION_VIEW : bslSelectedSite.id);
      history.replace({
        search: existingSearchParams.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.org, selectedSite, history, window.location.pathname, isOrgView]);

  useEffect(() => {
    const logoutCallback = () => {
      recordLogoutEvent({
        name: user.profile?.name ?? 'unknown',
        email: user.profile?.email ?? 'unknown',
        siteId: selectedSite.enterpriseUnitId,
        organizationId: user.org?.bslId ?? 'unknown',
      });
    };

    if (isAuthenticated && user.profile && user.org && selectedSite.enterpriseUnitId) {
      events.on(RccEventKeys.SIGNOUT, logoutCallback);
    }

    return () => {
      events.off(RccEventKeys.SIGNOUT, logoutCallback);
    };
  }, [isAuthenticated, events, user.profile, user.org, selectedSite.enterpriseUnitId]);

  const toggleSidebar = (): void => {
    setCollapsed((currentState) => !currentState);
  };

  const { oktaAuth } = useOktaAuth();
  const login = async () => {
    oktaAuth.signInWithRedirect();
  };

  const userProfileSettingsOptions: UserProfileOption[] = [];
  if (isPermitted(user, [CboRole.NOTIFICATION_GROUP_EDIT])) {
    const notificationSettingsUrl = user.org?.bslLongName
      ? `${routes.NOTIFICATION_SETTINGS}?organizationId=${user.org.bslLongName}`
      : routes.NOTIFICATION_SETTINGS;

    userProfileSettingsOptions.push({
      icon: <Settings />,
      label: t('admin.notificationSettingsPage.notificationSettingsTitle'),
      url: notificationSettingsUrl,
    });
  }

  userProfileSettingsOptions.push({
    icon: <Help />,
    label: t('buttonText.help'),
    onClick: () => {
      window.open(
        'https://developer.ncrvoyix.com/portals/dev-portal/help-center/topics?topic=Aloha%20Smart%20Manager',
        '_blank',
        'noopener'
      );
    },
  });
  return (
    <Box className='appbar-toggle' sx={outerBox(theme)}>
      {/* Do not render the application switcher in preview url */}
      {(isMobile || isCollapsed) && process.env.REACT_APP_USE_PREVIEW_ORG_SWITCHER !== 'true' ? (
        <ApplicationSwitcher isDisplayMode onClick={toggleSidebar} data-testid='app-switcher' />
      ) : (
        <IconButton
          onClick={toggleSidebar}
          data-testid='toggle-sidebar'
          name='sidebar-menu'
          aria-label='Toggle Navigation Menu'
          className={!isCollapsed ? 'open-sidebar' : 'sidebar'}
        >
          <MenuIcon sx={menuIcon} />
        </IconButton>
      )}
      {user.oktaStatus === 'authenticated' && (
        <Box sx={authenticatedActions(isMobile)} data-testid='authenticated-actions'>
          {userOrganizations?.length !== 1 && !isDrillDownView ? (
            <OrganizationSwitcher />
          ) : (
            <span>
              <Button disabled data-testid='drill-down-view-btn' startIcon={<DomainIcon />}>
                {organization?.displayName}
              </Button>
            </span>
          )}
          {/* When org switching or no org selected do not render site selector */}
          {showSitePicker &&
            // Use internal site selector if any is true: in a preview url, the bsl site picker feature flag (cbo-site-switcher) is off,
            // or in a route that does not yet support the new site picker (not in newSiteRoutes array)
            (useInternalSiteSelector ? <InternalSiteSelector isOrgView={isOrgView} /> : <BspSiteSwitcherWrapper />)}
          <UserProfileSettings inputOptions={userProfileSettingsOptions} />
          <IconButton aria-label='Notifications' sx={notificationsIcon} onClick={() => setRightPanel('notifications')}>
            <Badge color='primary' variant='dot' invisible={!hasUnreadNotifications}>
              <NotificationsIcon data-testid='notifications-icon' />
            </Badge>
          </IconButton>
        </Box>
      )}
      {user.oktaStatus === 'unauthenticated' && (
        <Button variant='outlined' sx={loginButton} data-testid='login-button' onClick={login}>
          {t('buttonText.login')}
        </Button>
      )}
    </Box>
  );
}
export default AppBar;
